import posthog from "posthog-js";
import config from "../helper/config";
import { initDebugHarness, recordEvent } from "./debug";

let initialized = false;

// Machine-traffic routes hit by external builder widgets on customer sites — never track.
// Mirrors the <TrackingHandler> routes registered in App.js. Most sit under /api/, but
// "/ProductTracking" (RingBuilder) does not, so it needs an explicit exact-match entry —
// a prefix check on "/api/" alone would silently start tracking it as human traffic.
const EXCLUDED_PATH_PREFIXES = ["/api/"];
const EXCLUDED_EXACT_PATHS = ["/ProductTracking"];

export function isExcludedPath(pathname) {
  const p = pathname || window.location.pathname || "";
  return (
    EXCLUDED_PATH_PREFIXES.some((prefix) => p.startsWith(prefix)) ||
    EXCLUDED_EXACT_PATHS.includes(p)
  );
}

export function initAnalytics() {
  if (initialized) return;
  if (!config.PostHogKey) return; // empty key in this block -> no-op
  if (isExcludedPath()) return; // /api/**/Tracking = builder widgets, not humans

  posthog.init(config.PostHogKey, {
    api_host: config.PostHogHost,
    ui_host: config.PostHogUiHost,

    // Per-tab identity store. Impersonation opens a second tab via window.open while the
    // admin tab stays live; shared localStorage would let the two identities overwrite
    // each other. This must stay sessionStorage.
    persistence: "sessionStorage",

    autocapture: true, // supplements the ~30 deliberate events with generic click/interaction capture
    capture_pageview: false, // manual — identity must settle first
    capture_pageleave: true, // explicit: default is 'if_capture_pageview'

    disable_session_recording: true, // started explicitly, see identity.js
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
      blockSelector: "[data-ph-block]",
      recordCrossOriginIframes: false, // builders are cross-origin; never record them
      sampleRate: 0.3,
      minimumDurationMilliseconds: 3000,
    },

    loaded: (ph) => {
      if (config.Environment !== "production") ph.debug();
    },

    // Feeds window.__phDebug's recentEvents() ring buffer (task #1749 two-tab QA gate).
    // Never mutates the event — always returns cr unchanged. No-op in production.
    before_send:
      config.Environment !== "production"
        ? (cr) => {
            if (cr) recordEvent(posthog.get_distinct_id(), cr.event, cr.properties);
            return cr;
          }
        : undefined,
  });

  // window.open() clones the opener's sessionStorage, so a fresh impersonation tab boots
  // holding the ADMIN's distinct_id. window.opener is truthy ONLY in the opened tab —
  // that is what keeps the admin tab (which also sees IMPERSONATE_HANDOFF in localStorage
  // for a short window) from resetting itself.
  try {
    if (window.opener && window.localStorage.getItem("IMPERSONATE_HANDOFF")) {
      posthog.reset(true); // true = regenerate device_id too
    }
  } catch (e) {
    // storage blocked — ignore
  }

  if (config.Environment !== "production") {
    try {
      window.sessionStorage.setItem("__ph_probe", "1");
      window.sessionStorage.removeItem("__ph_probe");
    } catch (e) {
      console.warn(
        "[analytics] sessionStorage unavailable — PostHog will fall back to " +
          "localStorage and impersonation identity WILL collide across tabs."
      );
    }
  }

  posthog.register({
    app_version: process.env.REACT_APP_VERSION || "0.1.0",
    environment: config.Environment,
  });

  initDebugHarness(posthog);

  initialized = true;
}

export default posthog;
